/**
 * ReviewPage — transaction review & Tally mapping.
 *
 * Nielsen Norman:
 *   #1 Visibility: live confidence badges + status icons on every row.
 *   #3 User control: undo by re-selecting; bulk assign; clear filters.
 *   #6 Recognition: cascading dropdowns — L2 filters L3, L3 filters L4, L4 filters L5.
 *   #7 Flexibility: change any level independently; L5 still auto-cascades L1-L4.
 *   #8 Minimalism: edit affordance shown inline; no modals for routine edits.
 * Apple HIG: inline selects feel native; consistent ring on focus; subtle row hover.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Download, Search, Filter, CheckCircle, AlertTriangle,
  HelpCircle, ChevronLeft, ChevronRight, Info, Trash2, X,
  ChevronDown, ChevronUp, FileSpreadsheet, FileText, Database,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import ChatPanel from '../components/ChatPanel';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog } from '../components/ui/dialog';
import api, { downloadFile } from '../utils/api';

const PAGE_SIZE = 50;

// ── Tally hierarchy (mirrors tally_master.py GROUP_HIERARCHY) ──────────────────
const GROUP_META = {
  'Sales Accounts':           { l1: 'P&L A/c',       l2: 'Income',       l3: 'Direct Income' },
  'Direct Incomes':           { l1: 'P&L A/c',       l2: 'Income',       l3: 'Direct Income' },
  'Indirect Incomes':         { l1: 'P&L A/c',       l2: 'Income',       l3: 'Indirect Income' },
  'Purchase Accounts':        { l1: 'P&L A/c',       l2: 'Expenditure',  l3: 'Direct Expenses' },
  'Direct Expenses':          { l1: 'P&L A/c',       l2: 'Expenditure',  l3: 'Direct Expenses' },
  'Indirect Expenses':        { l1: 'P&L A/c',       l2: 'Expenditure',  l3: 'Indirect Expenses' },
  'Fixed Assets':             { l1: 'Balance Sheet',  l2: 'Assets',       l3: 'Fixed Assets' },
  'Sundry Debtors':           { l1: 'Balance Sheet',  l2: 'Assets',       l3: 'Trade Receivables' },
  'Bank Accounts':            { l1: 'Balance Sheet',  l2: 'Assets',       l3: 'Banking & Cash' },
  'Cash-in-Hand':             { l1: 'Balance Sheet',  l2: 'Assets',       l3: 'Banking & Cash' },
  'Investments':              { l1: 'Balance Sheet',  l2: 'Assets',       l3: 'Investments' },
  'Loans & Advances (Asset)': { l1: 'Balance Sheet',  l2: 'Assets',       l3: 'Loans & Advances' },
  'Stock-in-Hand':            { l1: 'Balance Sheet',  l2: 'Assets',       l3: 'Inventory' },
  'Sundry Creditors':         { l1: 'Balance Sheet',  l2: 'Liabilities',  l3: 'Trade Payables' },
  'Loans (Liability)':        { l1: 'Balance Sheet',  l2: 'Liabilities',  l3: 'Loans & Borrowings' },
  'Bank OD':                  { l1: 'Balance Sheet',  l2: 'Liabilities',  l3: 'Bank OD' },
  'Current Liabilities':      { l1: 'Balance Sheet',  l2: 'Liabilities',  l3: 'Current Liabilities' },
  'Duties & Taxes':           { l1: 'Balance Sheet',  l2: 'Liabilities',  l3: 'Tax Liabilities' },
  'Provisions':               { l1: 'Balance Sheet',  l2: 'Liabilities',  l3: 'Provisions' },
  'Capital Account':          { l1: 'Balance Sheet',  l2: 'Capital',      l3: 'Proprietor Funds' },
  'Reserves & Surplus':       { l1: 'Balance Sheet',  l2: 'Capital',      l3: 'Reserves & Surplus' },
};

const L2_FOR_L1 = {
  'P&L A/c':      ['Income', 'Expenditure'],
  'Balance Sheet': ['Assets', 'Liabilities', 'Capital'],
};
// eslint-disable-next-line no-unused-vars
const L3_FOR_L2 = {
  'Income':      ['Direct Income', 'Indirect Income'],
  'Expenditure': ['Direct Expenses', 'Indirect Expenses'],
  'Assets':      ['Fixed Assets', 'Trade Receivables', 'Banking & Cash', 'Investments', 'Loans & Advances', 'Inventory'],
  'Liabilities': ['Trade Payables', 'Loans & Borrowings', 'Bank OD', 'Current Liabilities', 'Tax Liabilities', 'Provisions'],
  'Capital':     ['Proprietor Funds', 'Reserves & Surplus'],
};
// eslint-disable-next-line no-unused-vars
const L4_FOR_L3 = {
  'Direct Income':       ['Sales Accounts', 'Direct Incomes'],
  'Indirect Income':     ['Indirect Incomes'],
  'Direct Expenses':     ['Purchase Accounts', 'Direct Expenses'],
  'Indirect Expenses':   ['Indirect Expenses'],
  'Fixed Assets':        ['Fixed Assets'],
  'Trade Receivables':   ['Sundry Debtors'],
  'Banking & Cash':      ['Bank Accounts', 'Cash-in-Hand'],
  'Investments':         ['Investments'],
  'Loans & Advances':    ['Loans & Advances (Asset)'],
  'Inventory':           ['Stock-in-Hand'],
  'Trade Payables':      ['Sundry Creditors'],
  'Loans & Borrowings':  ['Loans (Liability)'],
  'Bank OD':             ['Bank OD'],
  'Current Liabilities': ['Current Liabilities'],
  'Tax Liabilities':     ['Duties & Taxes'],
  'Provisions':          ['Provisions'],
  'Proprietor Funds':    ['Capital Account'],
  'Reserves & Surplus':  ['Reserves & Surplus'],
};
const ALL_L2 = ['Income', 'Expenditure', 'Assets', 'Liabilities', 'Capital'];

// ── Indian Financial Year presets ──────────────────────────────────────────────
const FY_PRESETS = [
  { key: 'fy2526', label: 'FY 2025-26', from: '2025-04-01', to: '2026-03-31' },
  { key: 'fy2425', label: 'FY 2024-25', from: '2024-04-01', to: '2025-03-31' },
  { key: 'fy2324', label: 'FY 2023-24', from: '2023-04-01', to: '2024-03-31' },
  { key: 'q1fy26', label: 'Q1 Apr-Jun', from: '2025-04-01', to: '2025-06-30' },
  { key: 'q2fy26', label: 'Q2 Jul-Sep', from: '2025-07-01', to: '2025-09-30' },
  { key: 'q3fy26', label: 'Q3 Oct-Dec', from: '2025-10-01', to: '2025-12-31' },
  { key: 'q4fy26', label: 'Q4 Jan-Mar', from: '2026-01-01', to: '2026-03-31' },
];

// ── Clean Description parser (ICAI use case: extract payee from raw bank narration) ──
// Handles UPI, NEFT, RTGS, IMPS, NACH, ATM, CASH formats used by Indian banks.
function getCleanDescription(raw) {
  if (!raw) return '';
  const d = raw.trim();

  // UPI: "UPI/CR/123456/PRABIN RAM/HDFC/ref" or "UPI-PRABIN RAM-ref"
  let m = d.match(/^UPI[\/-](?:CR|DR)?[\/-]?\d*[\/-]([A-Za-z][A-Za-z0-9 .&'-]{2,40})[\/-]/i);
  if (m) return m[1].trim();

  // NEFT/RTGS: "NEFT/CR/HDFC0001234/PARTY NAME/..." or "NEFT-PARTY NAME-ref"
  m = d.match(/^(?:NEFT|RTGS)[\/-][A-Z0-9]*[\/-](?:[A-Z0-9]+[\/-])?([A-Za-z][A-Za-z0-9 .&'-]{2,50})[\/-]/i);
  if (m) return m[1].trim();

  // IMPS: "IMPS/ref/PARTY NAME/IFSC"
  m = d.match(/^IMPS[\/-]\d+[\/-]([A-Za-z][A-Za-z0-9 .&'-]{2,40})[\/-]/i);
  if (m) return m[1].trim();

  // NACH/ECS: "NACH DR HDFC BANK EMI" → "HDFC BANK EMI"
  m = d.match(/^(?:NACH|ECS)\s+(?:DR|CR)\s+(.{3,40}?)(?:\s+\d|$)/i);
  if (m) return m[1].trim();

  // ATM: "ATM/WDL/123/CANARA BANK MG ROAD BANGALORE"
  m = d.match(/^ATM[\/-](?:WDL|TXN)?[\/-]?\d*[\/-]?(.{4,40}?)(?:\s+\d{6}|$)/i);
  if (m) return 'ATM — ' + m[1].trim();

  // CHQ/CLG: cheque clearance
  m = d.match(/^(?:CLG|CHQ|CMS)[\/-](\d+)/i);
  if (m) return `Cheque ${m[1]}`;

  // CASH deposit/withdrawal
  if (/^CASH\s*(DEP|WDL|DEPOSIT|WITHDRAWAL)/i.test(d)) return 'Cash Transaction';

  // Salary keywords: return as-is but shortened
  if (/\bSALARY\b|\bPAYROLL\b/i.test(d)) return 'Salary Credit';

  // Fallback: first 40 chars cleaned of trailing slashes/refs
  return d.replace(/[\/-]\d{6,}.*$/, '').substring(0, 45).trim();
}

// ── Final Remark classifier (ICAI categories: Income/Expense/Drawings/Tax/etc.) ──
function getFinalRemark(t) {
  const d = (t.description || '').toUpperCase();
  const isDebit  = (t.debit  || 0) > 0;
  const isCredit = (t.credit || 0) > 0;
  const amt      = t.debit || t.credit || 0;

  // Tax payments — debit side only
  if (isDebit && /\bTDS\b|TAX\s*DEDUCTED|ADVANCE\s*TAX|SELF\s*ASSESS|CHALLAN|OLTAS|NSDL|TRACES|ITNS|IT\s*DEPT|\b19[0-9][A-Z]?\b/.test(d))
    return { label: 'Tax Payment', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' };

  // GST payment — debit
  if (isDebit && /\bCGST\b|\bSGST\b|\bIGST\b|GST\s*(PAY|CHALLAN|RETURN)|GSTP/.test(d))
    return { label: 'Tax Payment', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' };

  // Salary / payroll
  if (/\bSALARY\b|\bPAYROLL\b|\bSTIPEND\b|\bWAGES\b|\bHRMS\b|\bREMUNERATION\b/.test(d))
    return { label: isDebit ? 'Expense' : 'Income', cls: isDebit
      ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
      : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' };

  // Loan EMI — debit
  if (isDebit && /\bEMI\b|\bLOAN\s*REP|\bMORTGAGE\b|\bHOME\s*LOAN\b|\bNACH\b.*\b(LOAN|FINANCE|BANK|FINCORP)\b/.test(d))
    return { label: 'Loan Repayment', cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' };

  // Drawings: ATM cash withdrawal or self-transfer markers
  if (isDebit && (/^ATM[\/-]/.test(d) || /\bCASH\s*(WDL|WITHDRAWAL)\b/.test(d)))
    return { label: 'Drawings', cls: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300' };

  // Unsecured loan inflow (large round credit from individual UPI/NEFT)
  if (isCredit && amt >= 100000 && /^(UPI|NEFT|IMPS|RTGS)/.test(d))
    return { label: 'Unsecured Loan?', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' };

  // Fund transfer (self-transfer between own accounts)
  if (/SELF\s*TRANS|OWN\s*ACCT|TRANSFER\s*TO\s*(?:SELF|OWN)/.test(d))
    return { label: 'Fund Transfer', cls: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300' };

  // Generic income / expense by transaction direction
  if (isCredit) return { label: 'Income', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' };
  if (isDebit)  return { label: 'Expense', cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' };

  return { label: 'Review', cls: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' };
}

// ── Smart pattern detection (CA-specific intelligence) ─────────────────────────
// Each pattern runs entirely on the frontend — zero backend change needed.
const SMART_DETECT = [
  {
    key: 'gst',
    label: 'GST',
    icon: '🔷',
    desc: 'CGST / SGST / IGST payments & credits',
    cls: 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-700',
    // Require explicit GST tax codes — avoid matching generic "gst" substrings in party names
    test: t => /\bCGST\b|\bSGST\b|\bIGST\b|GST\s*(PAY|CHALLAN|RETURN|CR|DR)|GSTP\d|GSTIN\s*\d/i.test(t.description || ''),
  },
  {
    key: 'tds',
    label: 'TDS / Tax',
    icon: '📋',
    desc: 'TDS, advance tax, challan payments',
    cls: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-700',
    // \b word boundaries on section codes prevent matching account numbers containing "194"
    test: t => /\bTDS\b|TAX\s*DEDUCTED|ADVANCE\s*TAX|SELF\s*ASSESS|\bCHALLAN\b|OLTAS|NSDL|TRACES|ITNS\s*28[0-9]|\bIT\s*DEPT\b|INCOME.?TAX\s*(DEPT|PAY)|\b19[0-9][A-Z]?\b|\bTCS\b/i.test(t.description || ''),
  },
  {
    key: 'salary',
    label: 'Salary',
    icon: '💰',
    desc: 'Payroll, salary, wages entries',
    cls: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700',
    // Removed bare \bsal\b — too broad, matches "SALONI", "VISALAKSHI" etc.
    // SAL\/ catches "SAL/2025/05" style narrations used by corporate payroll
    test: t => /\bSALARY\b|SAL\/|SAL\s+CR|\bPAYROLL\b|\bSTIPEND\b|\bWAGES\b|\bHRMS\b|\bREMUNERATION\b|\bMONTHLY\s*PAY\b/i.test(t.description || ''),
  },
  {
    key: 'emi',
    label: 'EMI / Loan',
    icon: '🏠',
    desc: 'Loan EMIs, NACH debits, housing repayments',
    cls: 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-700',
    // Added NBFC names and NACH pattern (most EMIs deducted via NACH mandate)
    test: t => /\bEMI\b|\bLOAN\s*REP|\bMORTGAGE\b|\bREPAYMENT\b|\bINSTALLMENT\b|HDFC\s*HOME|LIC\s*HOUSING|\bNACH\b.*\b(LOAN|FINANCE|FINCORP|BANK|CAPITAL)\b|BAJAJ\s*FIN|TATA\s*CAPITAL|L&T\s*FIN|HDB\s*FIN|SHRIRAM|MAHINDRA\s*FIN/i.test(t.description || ''),
  },
  {
    key: 'round',
    label: 'Round Amounts',
    icon: '🔵',
    desc: 'Round-figure transactions ≥ ₹5,000 (audit flag)',
    cls: 'bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-300 border-sky-200 dark:border-sky-700',
    // ₹5,000 multiples (not just ₹10,000) — more useful for smaller CA clients
    test: t => { const a = t.debit || t.credit || 0; return a >= 5000 && a % 5000 === 0; },
  },
  {
    key: 'highvalue',
    label: '> ₹5 Lakh',
    icon: '⚠️',
    desc: 'High value transactions needing review',
    cls: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-700',
    test: t => (t.debit || t.credit || 0) >= 500000,
  },
  {
    key: 'cash',
    label: 'Cash',
    icon: '💵',
    desc: 'ATM withdrawals and cash deposits',
    cls: 'bg-lime-50 dark:bg-lime-900/20 text-lime-700 dark:text-lime-300 border-lime-200 dark:border-lime-700',
    test: t => /^ATM[\/-]|^CASH\s*(DEP|WDL|DEPOSIT|WITHDRAWAL)|\bATM\s*CASH\b|\bCASH\s*AT\b/i.test(t.description || ''),
  },
];

// Grouped option data for <optgroup> selects — all options always visible,
// organised so the user can navigate quickly without cascade restriction.
const L3_GROUPS = [
  { label: 'P&L — Income',                options: ['Direct Income', 'Indirect Income'] },
  { label: 'P&L — Expenditure',           options: ['Direct Expenses', 'Indirect Expenses'] },
  { label: 'Balance Sheet — Assets',      options: ['Fixed Assets', 'Trade Receivables', 'Banking & Cash', 'Investments', 'Loans & Advances', 'Inventory'] },
  { label: 'Balance Sheet — Liabilities', options: ['Trade Payables', 'Loans & Borrowings', 'Bank OD', 'Current Liabilities', 'Tax Liabilities', 'Provisions'] },
  { label: 'Balance Sheet — Capital',     options: ['Proprietor Funds', 'Reserves & Surplus'] },
];

const L4_GROUPS = [
  { label: 'Income',      options: ['Sales Accounts', 'Direct Incomes', 'Indirect Incomes'] },
  { label: 'Expenditure', options: ['Purchase Accounts', 'Direct Expenses', 'Indirect Expenses'] },
  { label: 'Assets',      options: ['Fixed Assets', 'Sundry Debtors', 'Bank Accounts', 'Cash-in-Hand', 'Investments', 'Loans & Advances (Asset)', 'Stock-in-Hand'] },
  { label: 'Liabilities', options: ['Sundry Creditors', 'Loans (Liability)', 'Bank OD', 'Current Liabilities', 'Duties & Taxes', 'Provisions'] },
  { label: 'Capital',     options: ['Capital Account', 'Reserves & Surplus'] },
];

// ── Shared select style ────────────────────────────────────────────────────────
const SEL = `
  w-full h-7 px-1.5 rounded-md border text-[12px]
  bg-[#F9F9F9] dark:bg-slate-800
  border-gray-200/80 dark:border-slate-700
  text-[#1D1D1F] dark:text-slate-200
  focus:outline-none focus:ring-1 focus:ring-sky-500 focus:border-sky-400 focus:bg-white dark:focus:bg-slate-700
  cursor-pointer transition-all duration-100
  hover:border-gray-400/70 dark:hover:border-slate-500
  shadow-sm
`;

// ── Formatters ─────────────────────────────────────────────────────────────────
const fmtINR = (v) => {
  if (v == null || v === '') return '';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR', minimumFractionDigits: 2,
  }).format(v);
};

// ── Badges ─────────────────────────────────────────────────────────────────────
function ConfBadge({ label }) {
  const map = {
    High:   'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
    Medium: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
    Low:    'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[label] || map.Low}`}>
      {label || 'Low'}
    </span>
  );
}

function StatusIcon({ status, aiVerified, correctionNote }) {
  return (
    <div className="flex items-center gap-1">
      {status === 'mapped'       && <CheckCircle  className="w-4 h-4 text-emerald-500" />}
      {status === 'needs_review' && <AlertTriangle className="w-4 h-4 text-amber-500" />}
      {status === 'unmapped'     && <HelpCircle    className="w-4 h-4 text-red-400" />}
      {(aiVerified || correctionNote) && (
        <span className="px-1 py-0.5 rounded text-xs font-bold bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400">AI</span>
      )}
    </div>
  );
}

const L7_COLORS = {
  UPI:      'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400',
  NEFT:     'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  RTGS:     'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400',
  IMPS:     'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400',
  ATM:      'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
  Cheque:   'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
  POS:      'bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-400',
  NACH:     'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400',
  Salary:   'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
  Interest: 'bg-lime-100 dark:bg-lime-900/30 text-lime-700 dark:text-lime-400',
  Tax:      'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  SWIFT:    'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400',
};
function L7Badge({ method }) {
  const cls = L7_COLORS[method] || 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400';
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{method || '—'}</span>;
}

const L1_COLORS = {
  'P&L A/c':      'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400',
  'Balance Sheet':'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400',
};
function L1Badge({ value }) {
  const cls = L1_COLORS[value] || 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400';
  return <span className={`px-2 py-0.5 rounded text-xs font-semibold ${cls}`}>{value || '—'}</span>;
}

function DrCrBadge({ value }) {
  if (value === 'Dr')
    return <span className="px-2 py-0.5 rounded text-xs font-bold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">Dr</span>;
  if (value === 'Cr')
    return <span className="px-2 py-0.5 rounded text-xs font-bold bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">Cr</span>;
  return <span className="text-slate-400">—</span>;
}

// ── SortIcon — MODULE LEVEL (not inside ReviewPage) ──────────────────────────
// Defining this inside ReviewPage would give React a new function reference on
// every render, forcing a full unmount+remount of every table-header that
// renders it. Module-level = stable reference = pure prop update only.
function SortIcon({ field, sortField, sortDir }) {
  if (sortField !== field) return <ChevronDown className="w-3 h-3 opacity-30" />;
  return sortDir === 'asc'
    ? <ChevronUp   className="w-3 h-3 text-sky-500" />
    : <ChevronDown className="w-3 h-3 text-sky-500" />;
}

// ── Main ───────────────────────────────────────────────────────────────────────
export default function ReviewPage() {
  const { id }     = useParams();
  const navigate   = useNavigate();

  const [statement,    setStatement]    = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [ledgers,      setLedgers]      = useState([]);

  // Basic filters
  const [search,       setSearch]       = useState('');
  const [typeFilter,   setTypeFilter]   = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [l7Filter,     setL7Filter]     = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [sortField,    setSortField]    = useState('date');
  const [sortDir,      setSortDir]      = useState('asc');

  // Advanced filters — date range
  const [dateFrom,    setDateFrom]    = useState('');
  const [dateTo,      setDateTo]      = useState('');
  const [fyPreset,    setFyPreset]    = useState('');

  // Advanced filters — amount
  const [amtMin,      setAmtMin]      = useState('');
  const [amtMax,      setAmtMax]      = useState('');

  // Advanced filters — smart detect (CA intelligence)
  const [activePatterns, setActivePatterns] = useState(new Set());

  // Advanced filters — tally classification
  const [filterL1,    setFilterL1]    = useState('');
  const [filterL2,    setFilterL2]    = useState('');

  // Advanced filters — AI confidence
  const [confFilter,  setConfFilter]  = useState(new Set());

  // Selection & bulk
  const [selected,       setSelected]       = useState(new Set());
  const [bulkLedger,     setBulkLedger]     = useState('');
  const [bulkApplying,   setBulkApplying]   = useState(false);
  // Final Remark user overrides — keyed by transaction id, falls back to getFinalRemark()
  const [remarkOverrides, setRemarkOverrides] = useState({});

  // Pagination
  const [page, setPage] = useState(1);

  // Dialogs
  const [showRules,    setShowRules]    = useState(false);
  const [rules,        setRules]        = useState([]);
  const [showGlossary, setShowGlossary] = useState(false);

  // ── Load data ────────────────────────────────────────────────────────────────
  useEffect(() => {
    Promise.all([
      api.get(`/statements/${id}`),
      api.get('/tally/ledgers'),
    ])
      .then(([stmtR, ledgerR]) => {
        setStatement(stmtR.data.statement);
        setTransactions(stmtR.data.transactions || []);
        setLedgers(ledgerR.data.ledgers || []);
      })
      .catch(() => toast.error('Failed to load statement'))
      .finally(() => setLoading(false));
  }, [id]);

  const loadRules = async () => {
    const r = await api.get('/mapping-rules');
    setRules(r.data.rules || []);
  };

  // ── FY preset handler ─────────────────────────────────────────────────────────
  const applyFyPreset = useCallback((key) => {
    const preset = FY_PRESETS.find(p => p.key === key);
    if (!preset) { setDateFrom(''); setDateTo(''); setFyPreset(''); return; }
    setDateFrom(preset.from);
    setDateTo(preset.to);
    setFyPreset(key);
    setPage(1);
  }, []);

  // ── Clear all filters ─────────────────────────────────────────────────────────
  const clearAllFilters = useCallback(() => {
    setSearch(''); setTypeFilter('all'); setStatusFilter('all'); setL7Filter('');
    setDateFrom(''); setDateTo(''); setFyPreset('');
    setAmtMin(''); setAmtMax('');
    setActivePatterns(new Set());
    setFilterL1(''); setFilterL2('');
    setConfFilter(new Set());
    setPage(1);
  }, []);

  // ── Active filter count (for badge on Advanced button) ────────────────────────
  const activeFilterCount = [
    search, typeFilter !== 'all', statusFilter !== 'all', l7Filter,
    dateFrom, dateTo, amtMin, amtMax,
    activePatterns.size > 0, filterL1, filterL2,
    confFilter.size > 0,
  ].filter(Boolean).length;

  // ── Filtering & sorting ───────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let txns = [...transactions];

    // Text search
    if (search) {
      const q = search.toLowerCase();
      txns = txns.filter(t =>
        (t.description || '').toLowerCase().includes(q) ||
        (t.category_l5 || t.ledger || '').toLowerCase().includes(q)
      );
    }

    // Basic type / status / payment method
    if (typeFilter === 'debits')  txns = txns.filter(t => t.debit > 0);
    if (typeFilter === 'credits') txns = txns.filter(t => t.credit > 0);
    if (statusFilter !== 'all')   txns = txns.filter(t => t.status === statusFilter);
    if (l7Filter)                 txns = txns.filter(t => t.category_l7 === l7Filter);

    // Date range
    if (dateFrom) txns = txns.filter(t => !t.date || t.date >= dateFrom);
    if (dateTo)   txns = txns.filter(t => !t.date || t.date <= dateTo);

    // Amount range
    if (amtMin !== '') txns = txns.filter(t => (t.debit || t.credit || 0) >= parseFloat(amtMin));
    if (amtMax !== '') txns = txns.filter(t => (t.debit || t.credit || 0) <= parseFloat(amtMax));

    // Smart CA patterns (OR logic — matches ANY active pattern)
    if (activePatterns.size > 0) {
      const patterns = SMART_DETECT.filter(p => activePatterns.has(p.key));
      txns = txns.filter(t => patterns.some(p => p.test(t)));
    }

    // Tally classification hierarchy
    if (filterL1) txns = txns.filter(t => t.category_l1 === filterL1);
    if (filterL2) txns = txns.filter(t => t.category_l2 === filterL2);

    // AI confidence
    if (confFilter.size > 0) {
      txns = txns.filter(t => confFilter.has(t.confidence_label || 'Low'));
    }

    // Sort
    txns.sort((a, b) => {
      let va = a[sortField] ?? '', vb = b[sortField] ?? '';
      if (typeof va === 'string') { va = va.toLowerCase(); vb = vb.toLowerCase(); }
      return sortDir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
    });
    return txns;
  }, [transactions, search, typeFilter, statusFilter, l7Filter,
      dateFrom, dateTo, amtMin, amtMax, activePatterns,
      filterL1, filterL2, confFilter, sortField, sortDir]);

  const paginated   = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages  = Math.ceil(filtered.length / PAGE_SIZE);

  // ── Stats ─────────────────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    total:   transactions.length,
    mapped:  transactions.filter(t => t.status === 'mapped').length,
    review:  transactions.filter(t => t.status === 'needs_review').length,
    unmapped:transactions.filter(t => t.status === 'unmapped').length,
  }), [transactions]);

  const filteredTotals = useMemo(() => ({
    debits:  filtered.reduce((s, t) => s + (t.debit  || 0), 0),
    credits: filtered.reduce((s, t) => s + (t.credit || 0), 0),
  }), [filtered]);

  // ── Selection ─────────────────────────────────────────────────────────────────
  const allPageSelected = paginated.length > 0 && paginated.every(t => selected.has(t.id));
  const someSelected    = selected.size > 0 && !allPageSelected;

  const toggleSelectAll = () => {
    if (allPageSelected) {
      setSelected(s => { const n = new Set(s); paginated.forEach(t => n.delete(t.id)); return n; });
    } else {
      setSelected(s => { const n = new Set(s); paginated.forEach(t => n.add(t.id)); return n; });
    }
  };
  const selectUnmapped = () => {
    const ids = transactions.filter(t => t.status === 'unmapped').map(t => t.id);
    setSelected(new Set(ids));
    toast.info(`Selected ${ids.length} unmapped transactions`);
  };

  // ── L5 Ledger update (cascades L1–L4) ────────────────────────────────────────
  const updateLedger = useCallback(async (txnId, ledger) => {
    if (!ledger) return;
    try {
      await api.put(`/transactions/${txnId}`, { ledger });
      setTransactions(prev => prev.map(t => {
        if (t.id !== txnId) return t;
        const entry = ledgers.find(l => l.ledger === ledger);
        const g     = entry?.group || '';
        const meta  = GROUP_META[g] || {};
        return {
          ...t,
          ledger,
          category_l5:    ledger,
          tally_group:    g,
          category_l4:    g,
          category_l1:    meta.l1 || t.category_l1,
          category_l2:    meta.l2 || t.category_l2,
          category_l3:    meta.l3 || t.category_l3,
          status:         'mapped',
          confidence_label: 'High',
        };
      }));
    } catch {
      toast.error('Failed to update ledger');
    }
  }, [ledgers]);

  // ── L4 Tally Group update (cascades L1–L3; keeps L5 unchanged) ───────────────
  const updateL4 = useCallback(async (txnId, newGroup) => {
    if (!newGroup) return;
    const meta = GROUP_META[newGroup];
    if (!meta) return;
    try {
      const updates = {
        tally_group:  newGroup,
        category_l1:  meta.l1,
        category_l2:  meta.l2,
        category_l3:  meta.l3,
        category_l4:  newGroup,
      };
      await api.put(`/transactions/${txnId}`, updates);
      setTransactions(prev => prev.map(t => t.id === txnId ? { ...t, ...updates } : t));
    } catch {
      toast.error('Failed to update group');
    }
  }, []);

  // ── L3 Sub-Type update (independent; doesn't cascade) ────────────────────────
  const updateL3 = useCallback(async (txnId, newL3) => {
    if (!newL3) return;
    try {
      await api.put(`/transactions/${txnId}`, { category_l3: newL3 });
      setTransactions(prev => prev.map(t => t.id === txnId ? { ...t, category_l3: newL3 } : t));
    } catch {
      toast.error('Failed to update sub-type');
    }
  }, []);

  // ── L2 Category update (independent; doesn't cascade) ────────────────────────
  const updateL2 = useCallback(async (txnId, newL2) => {
    if (!newL2) return;
    try {
      await api.put(`/transactions/${txnId}`, { category_l2: newL2 });
      setTransactions(prev => prev.map(t => t.id === txnId ? { ...t, category_l2: newL2 } : t));
    } catch {
      toast.error('Failed to update category');
    }
  }, []);

  // ── Bulk update ───────────────────────────────────────────────────────────────
  const applyBulk = async () => {
    if (!bulkLedger || selected.size === 0) return;
    setBulkApplying(true);
    try {
      await api.put('/transactions/bulk-update', {
        transaction_ids: Array.from(selected),
        ledger: bulkLedger,
      });
      const entry = ledgers.find(l => l.ledger === bulkLedger);
      const g     = entry?.group || '';
      const meta  = GROUP_META[g] || {};
      setTransactions(prev => prev.map(t =>
        selected.has(t.id)
          ? { ...t, ledger: bulkLedger, category_l5: bulkLedger, tally_group: g, category_l4: g,
              category_l1: meta.l1 || t.category_l1,
              category_l2: meta.l2 || t.category_l2,
              category_l3: meta.l3 || t.category_l3,
              status: 'mapped', confidence_label: 'High' }
          : t
      ));
      toast.success(`Updated ${selected.size} transactions to "${bulkLedger}"`);
      setSelected(new Set());
      setBulkLedger('');
    } catch {
      toast.error('Bulk update failed');
    } finally {
      setBulkApplying(false);
    }
  };

  // ── Downloads ─────────────────────────────────────────────────────────────────
  const handleDownload = async (path, filename) => {
    try { await downloadFile(path, filename); }
    catch { toast.error('Download failed. Please try again.'); }
  };

  const sortToggle = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  // SortIcon is defined at module level (below) to avoid
  // React remounting it on every ReviewPage re-render.

  // ── Loading / error states ────────────────────────────────────────────────────
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-96">
          <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }
  if (!statement) {
    return (
      <DashboardLayout>
        <div className="text-center py-20">
          <p className="text-slate-500">Statement not found.</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const aiCorrections = statement.verification_corrections || 0;
  const aiRemovals    = statement.verification_removals    || 0;
  const aiFlagged     = statement.verification_flagged     || 0;
  const hasAiActivity = aiCorrections + aiRemovals + aiFlagged > 0;
  const bankName      = statement.bank_name || 'Bank';
  const acct          = statement.account_number || '';

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <>
    <DashboardLayout>
      <div className="w-full min-w-0 max-w-full">

      {/* ── Page header ──────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-semibold tracking-tight text-[#1D1D1F] dark:text-slate-100">
              {bankName}{acct ? ` — ${acct}` : ''}
            </h1>
            <p className="text-[13px] text-[#6E6E73] dark:text-slate-400 mt-0.5">
              {statement.statement_period || 'Statement Period Unknown'}
            </p>
          </div>
        </div>

        {/* Export buttons */}
        <div className="flex flex-wrap items-center gap-2">
          {[
            { label: 'Excel',        icon: <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" />, path: `/api/statements/${id}/export/excel`,        file: `${bankName}_${acct}.xlsx` },
            { label: 'Tally XML',    icon: <FileText        className="w-3.5 h-3.5 text-sky-600" />,     path: `/api/statements/${id}/export/tally`,         file: `${bankName}_Tally.xml` },
            { label: 'Tally CSV',    icon: <FileText        className="w-3.5 h-3.5 text-blue-600" />,    path: `/api/statements/${id}/export/tally-csv`,     file: `${bankName}_Tally.csv` },
            { label: 'Ledger Master',icon: <Database        className="w-3.5 h-3.5 text-violet-600" />,  path: `/api/statements/${id}/export/ledger-master`, file: `${bankName}_Ledger.xlsx` },
            { label: 'JSON',         icon: <Download        className="w-3.5 h-3.5 text-slate-500" />,   path: `/api/statements/${id}/export/json`,           file: `Statement_${id.slice(0,8)}.json` },
          ].map(({ label, icon, path, file }) => (
            <button key={label}
              onClick={() => handleDownload(path, file)}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-gray-200/80 dark:border-slate-700 bg-white dark:bg-slate-800 text-[13px] font-medium text-[#1D1D1F] dark:text-slate-200 shadow-sm hover:bg-[#F5F5F7] dark:hover:bg-slate-700 hover:shadow active:scale-[0.97] transition-all duration-100"
            >
              {icon} {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── AI banner ────────────────────────────────────────────────────────── */}
      {hasAiActivity && (
        <div className="mb-4 p-4 rounded-xl bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-violet-600 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-white text-xs font-bold">AI</span>
            </div>
            <div>
              <p className="text-sm font-semibold text-violet-800 dark:text-violet-300">AI Verification Applied</p>
              <div className="flex flex-wrap gap-4 mt-1 text-xs text-violet-700 dark:text-violet-400">
                {aiCorrections > 0 && <span>✓ {aiCorrections} debit/credit corrections</span>}
                {aiRemovals    > 0 && <span>🗑 {aiRemovals} artifact rows removed</span>}
                {aiFlagged     > 0 && <span>⚠ {aiFlagged} rows flagged for review</span>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Stats cards (click to filter) ────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {[
          { label: 'Total',        value: stats.total,    filter: 'all',          cls: '' },
          { label: 'Mapped',       value: stats.mapped,   filter: 'mapped',       cls: 'border-emerald-200 dark:border-emerald-800' },
          { label: 'Needs Review', value: stats.review,   filter: 'needs_review', cls: 'border-amber-200 dark:border-amber-800' },
          { label: 'Unmapped',     value: stats.unmapped, filter: 'unmapped',     cls: 'border-red-200 dark:border-red-800' },
        ].map(({ label, value, filter, cls }) => (
          <div
            key={label}
            onClick={() => { setStatusFilter(filter); setPage(1); }}
            className={`
              bg-white dark:bg-slate-900 rounded-xl border
              px-5 py-4 cursor-pointer transition-all duration-150
              shadow-sm hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98]
              ${statusFilter === filter
                ? 'ring-1 ring-sky-500 border-sky-400/50 dark:border-sky-600'
                : `border-gray-200/80 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600 ${cls}`}
            `}
          >
            <p className="text-2xl font-semibold tabular-nums tracking-tight text-[#1D1D1F] dark:text-slate-100">{value.toLocaleString()}</p>
            <p className="text-[11px] font-medium uppercase tracking-wide text-[#6E6E73] dark:text-slate-400 mt-1">{label}</p>
          </div>
        ))}
      </div>

      {/* ── Filter bar ───────────────────────────────────────────────────────── */}
      <div className="w-full min-w-0 bg-white dark:bg-slate-900 rounded-xl border border-gray-200/80 dark:border-slate-700 p-4 mb-4 space-y-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[160px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search description or ledger…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-full h-9 pl-9 pr-3 rounded-full border-0 text-sm bg-[#F5F5F7] dark:bg-slate-800 text-[#1D1D1F] dark:text-slate-100 placeholder:text-[#8E8E93] dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:bg-white dark:focus:bg-slate-700 transition-colors"
            />
          </div>

          {[
            { value: typeFilter,   setter: setTypeFilter,   opts: [['all','All Types'],['debits','Debits'],['credits','Credits']] },
            { value: statusFilter, setter: setStatusFilter, opts: [['all','All Status'],['mapped','Mapped'],['needs_review','Review'],['unmapped','Unmapped']] },
          ].map(({ value, setter, opts }, idx) => (
            <select
              key={idx}
              value={value}
              onChange={(e) => { setter(e.target.value); setPage(1); }}
              className="h-9 px-3 rounded-full border border-gray-200/80 dark:border-slate-700 text-sm bg-white dark:bg-slate-800 text-[#1D1D1F] dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-sky-500 shadow-sm"
            >
              {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          ))}

          <Button variant="outline" size="sm" onClick={selectUnmapped} className="gap-1.5">
            <HelpCircle className="w-3.5 h-3.5" /> Select Unmapped
          </Button>

          <button
            onClick={() => setShowAdvanced(v => !v)}
            className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full border text-[13px] font-medium transition-all duration-100 shadow-sm active:scale-[0.97]
              ${showAdvanced
                ? 'bg-sky-600 text-white border-sky-600'
                : 'bg-white dark:bg-slate-800 text-[#1D1D1F] dark:text-slate-200 border-gray-200/80 dark:border-slate-700 hover:border-sky-400 hover:text-sky-600'}`}
          >
            <Filter className="w-3.5 h-3.5" />
            Advanced
            {activeFilterCount > 0 && (
              <span className={`ml-0.5 min-w-[18px] h-[18px] rounded-full text-[10px] font-bold flex items-center justify-center px-1
                ${showAdvanced ? 'bg-white/20 text-white' : 'bg-sky-600 text-white'}`}>
                {activeFilterCount}
              </span>
            )}
            {showAdvanced ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>

          {activeFilterCount > 0 && (
            <button onClick={clearAllFilters}
              className="inline-flex items-center gap-1 h-9 px-3 rounded-full text-[13px] font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
              <X className="w-3.5 h-3.5" /> Clear all
            </button>
          )}
        </div>

        {showAdvanced && (
          <div className="border-t border-gray-100 dark:border-slate-800 pt-4 space-y-5">

            {/* ── Period ── */}
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-[#6E6E73] dark:text-slate-400 mb-2 flex items-center gap-1.5">
                📅 Period
              </p>
              <div className="flex flex-wrap gap-1.5 mb-2.5">
                {FY_PRESETS.map(p => (
                  <button key={p.key}
                    onClick={() => applyFyPreset(fyPreset === p.key ? '' : p.key)}
                    className={`px-3 py-1 rounded-full text-[12px] font-medium border transition-all duration-100 active:scale-[0.97]
                      ${fyPreset === p.key
                        ? 'bg-sky-600 text-white border-sky-600 shadow-sm'
                        : 'bg-white dark:bg-slate-800 text-[#1D1D1F] dark:text-slate-200 border-gray-200/80 dark:border-slate-700 hover:border-sky-400 hover:text-sky-600'}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-[#6E6E73] dark:text-slate-400 w-14">Custom</span>
                <input type="date" value={dateFrom}
                  onChange={e => { setDateFrom(e.target.value); setFyPreset('custom'); setPage(1); }}
                  className="h-7 px-2 rounded-md border border-gray-200/80 dark:border-slate-700 text-[12px] bg-white dark:bg-slate-800 text-[#1D1D1F] dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500 shadow-sm" />
                <span className="text-[#6E6E73] dark:text-slate-500 text-sm">→</span>
                <input type="date" value={dateTo}
                  onChange={e => { setDateTo(e.target.value); setFyPreset('custom'); setPage(1); }}
                  className="h-7 px-2 rounded-md border border-gray-200/80 dark:border-slate-700 text-[12px] bg-white dark:bg-slate-800 text-[#1D1D1F] dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500 shadow-sm" />
                {(dateFrom || dateTo) && (
                  <button onClick={() => { setDateFrom(''); setDateTo(''); setFyPreset(''); setPage(1); }}
                    className="text-[#6E6E73] hover:text-red-500 transition-colors">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* ── Amount ── */}
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-[#6E6E73] dark:text-slate-400 mb-2">
                ₹ Amount
              </p>
              <div className="flex flex-wrap gap-1.5 mb-2.5">
                {[
                  { label: '> ₹1 Lakh',  min: 100000,   max: '' },
                  { label: '> ₹10 Lakh', min: 1000000,  max: '' },
                  { label: '> ₹1 Crore', min: 10000000, max: '' },
                ].map(p => (
                  <button key={p.label}
                    onClick={() => { setAmtMin(Number(amtMin) === p.min ? '' : String(p.min)); setAmtMax(p.max); setPage(1); }}
                    className={`px-3 py-1 rounded-full text-[12px] font-medium border transition-all duration-100 active:scale-[0.97]
                      ${Number(amtMin) === p.min
                        ? 'bg-sky-600 text-white border-sky-600 shadow-sm'
                        : 'bg-white dark:bg-slate-800 text-[#1D1D1F] dark:text-slate-200 border-gray-200/80 dark:border-slate-700 hover:border-sky-400 hover:text-sky-600'}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-[#6E6E73] dark:text-slate-400 w-14">Range ₹</span>
                <input type="number" placeholder="Min" value={amtMin}
                  onChange={e => { setAmtMin(e.target.value); setPage(1); }}
                  className="w-28 h-7 px-2 rounded-md border border-gray-200/80 dark:border-slate-700 text-[12px] bg-white dark:bg-slate-800 text-[#1D1D1F] dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500 shadow-sm placeholder:text-[#8E8E93]" />
                <span className="text-[#6E6E73] dark:text-slate-500">—</span>
                <input type="number" placeholder="Max" value={amtMax}
                  onChange={e => { setAmtMax(e.target.value); setPage(1); }}
                  className="w-28 h-7 px-2 rounded-md border border-gray-200/80 dark:border-slate-700 text-[12px] bg-white dark:bg-slate-800 text-[#1D1D1F] dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500 shadow-sm placeholder:text-[#8E8E93]" />
              </div>
            </div>

            {/* ── Smart CA Detection ── */}
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-[#6E6E73] dark:text-slate-400 mb-1">
                🧠 Smart CA Detect
              </p>
              <p className="text-[11px] text-[#8E8E93] dark:text-slate-500 mb-2.5">
                AI-free pattern matching — click multiple to find any matching transaction.
              </p>
              <div className="flex flex-wrap gap-2">
                {SMART_DETECT.map(p => {
                  const active = activePatterns.has(p.key);
                  const count  = transactions.filter(p.test).length;
                  return (
                    <button key={p.key}
                      onClick={() => {
                        const n = new Set(activePatterns);
                        active ? n.delete(p.key) : n.add(p.key);
                        setActivePatterns(n);
                        setPage(1);
                      }}
                      title={p.desc}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[12px] font-medium transition-all duration-100 active:scale-[0.97] shadow-sm
                        ${active
                          ? 'bg-sky-600 text-white border-sky-600 shadow-md'
                          : `${p.cls} hover:shadow-md`}`}
                    >
                      <span>{p.icon} {p.label}</span>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center
                        ${active ? 'bg-white/25 text-white' : 'bg-white/70 dark:bg-slate-900/50'}`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── Tally Classification + L7 + Confidence in a grid ── */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

              {/* Tally hierarchy */}
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-[#6E6E73] dark:text-slate-400 mb-2">
                  📊 Tally Class
                </p>
                <div className="space-y-1.5">
                  <select value={filterL1}
                    onChange={e => { setFilterL1(e.target.value); setFilterL2(''); setPage(1); }}
                    className="w-full h-8 px-2 rounded-md border border-gray-200/80 dark:border-slate-700 text-[12px] bg-white dark:bg-slate-800 text-[#1D1D1F] dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500 shadow-sm">
                    <option value="">All L1 (Statement)</option>
                    <option>P&L A/c</option>
                    <option>Balance Sheet</option>
                  </select>
                  <select value={filterL2}
                    onChange={e => { setFilterL2(e.target.value); setPage(1); }}
                    className="w-full h-8 px-2 rounded-md border border-gray-200/80 dark:border-slate-700 text-[12px] bg-white dark:bg-slate-800 text-[#1D1D1F] dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500 shadow-sm">
                    <option value="">All L2 (Category)</option>
                    {(filterL1 ? (L2_FOR_L1[filterL1] || ALL_L2) : ALL_L2).map(o => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Payment method */}
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-[#6E6E73] dark:text-slate-400 mb-2">
                  💳 Payment Method (L7)
                </p>
                <select value={l7Filter}
                  onChange={e => { setL7Filter(e.target.value); setPage(1); }}
                  className="w-full h-8 px-2 rounded-md border border-gray-200/80 dark:border-slate-700 text-[12px] bg-white dark:bg-slate-800 text-[#1D1D1F] dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500 shadow-sm">
                  <option value="">All Methods</option>
                  {['UPI','NEFT','RTGS','IMPS','ATM','Cheque','POS','NACH','Salary','Interest','Tax','SWIFT'].map(m => (
                    <option key={m} value={m}>{m} ({transactions.filter(t => t.category_l7 === m).length})</option>
                  ))}
                </select>
              </div>

              {/* AI Confidence */}
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-[#6E6E73] dark:text-slate-400 mb-2">
                  🤖 AI Confidence
                </p>
                <div className="space-y-1.5">
                  {[
                    { level: 'High',   cls: 'text-emerald-600 dark:text-emerald-400' },
                    { level: 'Medium', cls: 'text-amber-600 dark:text-amber-400' },
                    { level: 'Low',    cls: 'text-red-600 dark:text-red-400' },
                  ].map(({ level, cls }) => {
                    const active = confFilter.has(level);
                    const count  = transactions.filter(t => (t.confidence_label || 'Low') === level).length;
                    return (
                      <label key={level} className="flex items-center gap-2 cursor-pointer select-none group">
                        <input type="checkbox" checked={active}
                          onChange={() => {
                            const n = new Set(confFilter);
                            active ? n.delete(level) : n.add(level);
                            setConfFilter(n); setPage(1);
                          }}
                          className="rounded border-gray-300 dark:border-slate-600 accent-sky-600" />
                        <span className={`text-[12px] font-medium ${cls}`}>{level}</span>
                        <span className="text-[11px] text-[#8E8E93] dark:text-slate-500">({count})</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ── Sort ── */}
            <div className="flex items-center gap-2 pt-2 border-t border-gray-100 dark:border-slate-800">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-[#6E6E73] dark:text-slate-400">Sort by</span>
              <select value={sortField} onChange={e => setSortField(e.target.value)}
                className="h-7 px-2 rounded-md border border-gray-200/80 dark:border-slate-700 text-[12px] bg-white dark:bg-slate-800 text-[#1D1D1F] dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500 shadow-sm">
                <option value="date">Date</option>
                <option value="debit">Debit Amount</option>
                <option value="credit">Credit Amount</option>
                <option value="description">Description</option>
                <option value="confidence_label">Confidence</option>
              </select>
              <button onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
                className="h-7 w-7 rounded-md border border-gray-200/80 dark:border-slate-700 bg-white dark:bg-slate-800 flex items-center justify-center hover:bg-[#F5F5F7] dark:hover:bg-slate-700 shadow-sm transition-colors">
                {sortDir === 'asc' ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>
            </div>

          </div>
        )}
      </div>

      {/* ── Bulk assign bar ──────────────────────────────────────────────────── */}
      {selected.size > 0 && (
        <div className="bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 rounded-xl p-3 mb-4 flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-sky-700 dark:text-sky-400">{selected.size} selected</span>
          <select
            value={bulkLedger}
            onChange={(e) => setBulkLedger(e.target.value)}
            className="flex-1 min-w-48 h-9 px-3 rounded-lg border text-sm bg-white dark:bg-slate-800 border-sky-300 dark:border-sky-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-sky-500"
          >
            <option value="">— Select ledger to assign —</option>
            {ledgers.map(l => <option key={l.ledger} value={l.ledger}>{l.ledger} ({l.group})</option>)}
          </select>
          <Button size="sm" onClick={applyBulk} loading={bulkApplying} disabled={!bulkLedger}>Apply</Button>
          <button onClick={() => setSelected(new Set())} className="text-sm text-sky-600 hover:text-sky-800 dark:hover:text-sky-300">Clear</button>
        </div>
      )}

      {/* ── Transaction table ────────────────────────────────────────────────── */}
      <div className="w-full min-w-0 bg-white dark:bg-slate-900 rounded-xl border border-gray-200/80 dark:border-slate-700 overflow-hidden mb-4 shadow-sm">
        {filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Search className="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">No transactions match your filters</p>
            <Button variant="outline" size="sm" className="mt-4"
              onClick={() => { setSearch(''); setTypeFilter('all'); setStatusFilter('all'); setL7Filter(''); }}>
              Clear Filters
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-200/80 dark:border-slate-700/60 bg-[#F5F5F7] dark:bg-slate-800/80">
                  <th className="pl-4 pr-2 py-3">
                    <Checkbox checked={allPageSelected} indeterminate={someSelected} onChange={toggleSelectAll} />
                  </th>

                  {/* Sortable core columns */}
                  {[
                    ['date',        'Date'],
                    ['description', 'Description'],
                  ].map(([f, lbl]) => (
                    <th key={f} onClick={() => sortToggle(f)}
                      className="px-3 py-3 text-left text-[11px] font-semibold text-[#6E6E73] dark:text-slate-400 uppercase tracking-wider cursor-pointer whitespace-nowrap select-none">
                      <div className="flex items-center gap-1">{lbl} <SortIcon field={f} sortField={sortField} sortDir={sortDir} /></div>
                    </th>
                  ))}

                  {/* AI-derived: Clean Description */}
                  <th className="px-3 py-3 text-left text-[11px] font-semibold text-sky-600 dark:text-sky-400 uppercase tracking-wider whitespace-nowrap min-w-[120px]">
                    Clean Desc
                  </th>

                  {[
                    ['debit',   'Debit'],
                    ['credit',  'Credit'],
                    ['balance', 'Balance'],
                  ].map(([f, lbl]) => (
                    <th key={f} onClick={() => sortToggle(f)}
                      className="px-3 py-3 text-left text-[11px] font-semibold text-[#6E6E73] dark:text-slate-400 uppercase tracking-wider cursor-pointer whitespace-nowrap select-none">
                      <div className="flex items-center gap-1">{lbl} <SortIcon field={f} sortField={sortField} sortDir={sortDir} /></div>
                    </th>
                  ))}

                  {/* Classification columns — ✎ marks editable */}
                  <th className="px-3 py-3 text-left text-[11px] font-semibold text-[#6E6E73] dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">L1 Stmt</th>
                  <th className="px-3 py-3 text-left text-[11px] font-semibold text-[#6E6E73] dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">
                    <span title="L2 — Financial category (editable)">L2 ✎</span>
                  </th>
                  <th className="px-3 py-3 text-left text-[11px] font-semibold text-[#6E6E73] dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">
                    <span title="L3 — Sub-type (editable)">L3 ✎</span>
                  </th>
                  <th className="px-3 py-3 text-left text-[11px] font-semibold text-[#6E6E73] dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">
                    <span title="L4 — Tally Group (editable, cascades L1–L3)">L4 ✎</span>
                  </th>
                  <th className="px-3 py-3 text-left text-[11px] font-semibold text-[#6E6E73] dark:text-slate-400 uppercase tracking-wider whitespace-nowrap min-w-[180px]">
                    <span title="L5 — Tally Ledger (editable, cascades L1–L4)">L5 Ledger ✎</span>
                  </th>
                  <th className="px-3 py-3 text-left text-[11px] font-semibold text-sky-600 dark:text-sky-400 uppercase tracking-wider whitespace-nowrap min-w-[140px]">
                    <span title="Final Remark — AI-suggested, editable">Final Remark ✎</span>
                  </th>
                  <th className="px-3 py-3 text-left text-[11px] font-semibold text-[#6E6E73] dark:text-slate-400 uppercase tracking-wider">L6</th>
                  <th className="px-3 py-3 text-left text-[11px] font-semibold text-[#6E6E73] dark:text-slate-400 uppercase tracking-wider">L7</th>
                  <th className="px-3 py-3 text-left text-[11px] font-semibold text-[#6E6E73] dark:text-slate-400 uppercase tracking-wider">Conf</th>
                  <th className="px-3 py-3 text-left text-[11px] font-semibold text-[#6E6E73] dark:text-slate-400 uppercase tracking-wider">Status</th>
                </tr>
              </thead>

              <tbody>
                {paginated.map((t, i) => {
                  // All levels always show complete options — full flexibility (Nielsen Norman #7)

                  return (
                    <tr
                      key={t.id}
                      className={`
                        border-b border-gray-100/80 dark:border-slate-800/50
                        transition-colors duration-75
                        ${selected.has(t.id)
                          ? 'bg-sky-50 dark:bg-sky-900/20'
                          : i % 2 === 0
                            ? 'bg-white dark:bg-slate-900'
                            : 'bg-[#F9F9FA] dark:bg-slate-800/25'}
                        hover:bg-blue-50/40 dark:hover:bg-slate-700/30
                      `}
                    >
                      {/* Checkbox */}
                      <td className="pl-4 pr-2 py-2.5">
                        <Checkbox
                          checked={selected.has(t.id)}
                          onChange={() => setSelected(s => {
                            const n = new Set(s);
                            n.has(t.id) ? n.delete(t.id) : n.add(t.id);
                            return n;
                          })}
                        />
                      </td>

                      {/* Date */}
                      <td className="px-3 py-2.5 text-slate-600 dark:text-slate-400 whitespace-nowrap font-mono text-xs">
                        {t.date || '—'}
                      </td>

                      {/* Description */}
                      <td className="px-3 py-2.5 max-w-[200px]">
                        <p className="truncate text-slate-900 dark:text-slate-100 text-xs" title={t.description}>{t.description || '—'}</p>
                        {t.cheque_ref && <p className="text-slate-400 text-[11px] truncate">{t.cheque_ref}</p>}
                        {t.correction_note && (
                          <p className="text-violet-500 dark:text-violet-400 text-[11px] truncate mt-0.5" title={t.correction_note}>
                            💡 {t.correction_note}
                          </p>
                        )}
                      </td>

                      {/* Clean Description */}
                      <td className="px-3 py-2.5 max-w-[140px]">
                        <p className="truncate text-sky-700 dark:text-sky-300 text-xs font-medium" title={getCleanDescription(t.description)}>
                          {getCleanDescription(t.description) || '—'}
                        </p>
                      </td>


                      {/* Debit / Credit / Balance */}
                      <td className="px-3 py-2.5 text-red-600 dark:text-red-400 font-medium whitespace-nowrap text-right tabular-nums">
                        {t.debit  ? fmtINR(t.debit)  : ''}
                      </td>
                      <td className="px-3 py-2.5 text-emerald-600 dark:text-emerald-400 font-medium whitespace-nowrap text-right tabular-nums">
                        {t.credit ? fmtINR(t.credit) : ''}
                      </td>
                      <td className="px-3 py-2.5 text-slate-500 dark:text-slate-400 whitespace-nowrap text-right tabular-nums">
                        {t.balance != null ? fmtINR(t.balance) : ''}
                      </td>

                      {/* L1 — read only (derived from L2/L5) */}
                      <td className="px-3 py-2.5">
                        <L1Badge value={t.category_l1} />
                      </td>

                      {/* L2 — editable dropdown, all 5 categories always available */}
                      <td className="px-3 py-2.5 min-w-[120px]">
                        <select
                          value={t.category_l2 || ''}
                          onChange={(e) => updateL2(t.id, e.target.value)}
                          className={SEL}
                          title="L2 Category — all options available"
                        >
                          <option value="">— Category —</option>
                          <optgroup label="P&L A/c">
                            <option>Income</option>
                            <option>Expenditure</option>
                          </optgroup>
                          <optgroup label="Balance Sheet">
                            <option>Assets</option>
                            <option>Liabilities</option>
                            <option>Capital</option>
                          </optgroup>
                        </select>
                      </td>

                      {/* L3 — editable dropdown, all 16 sub-types grouped by statement */}
                      <td className="px-3 py-2.5 min-w-[150px]">
                        <select
                          value={t.category_l3 || ''}
                          onChange={(e) => updateL3(t.id, e.target.value)}
                          className={SEL}
                          title="L3 Sub-type — all options available"
                        >
                          <option value="">— Sub-type —</option>
                          {L3_GROUPS.map(g => (
                            <optgroup key={g.label} label={g.label}>
                              {g.options.map(o => <option key={o} value={o}>{o}</option>)}
                            </optgroup>
                          ))}
                        </select>
                      </td>

                      {/* L4 — editable dropdown, all 21 Tally groups grouped by type */}
                      <td className="px-3 py-2.5 min-w-[165px]">
                        <select
                          value={t.category_l4 || t.tally_group || ''}
                          onChange={(e) => updateL4(t.id, e.target.value)}
                          className={SEL}
                          title="L4 Tally Group — all groups available; selecting auto-fills L1–L3"
                        >
                          <option value="">— Tally Group —</option>
                          {L4_GROUPS.map(g => (
                            <optgroup key={g.label} label={g.label}>
                              {g.options.map(o => <option key={o} value={o}>{o}</option>)}
                            </optgroup>
                          ))}
                        </select>
                      </td>

                      {/* L5 — primary ledger dropdown, cascades L1–L4 */}
                      <td className="px-3 py-2.5 min-w-[180px]">
                        <select
                          value={t.category_l5 || t.ledger || ''}
                          onChange={(e) => updateLedger(t.id, e.target.value)}
                          className={SEL}
                          title="L5 Ledger — changing this updates L1–L4"
                        >
                          <option value="">— Ledger —</option>
                          {ledgers.map(l => (
                            <option key={l.ledger} value={l.ledger}>{l.ledger}</option>
                          ))}
                        </select>
                      </td>

                      {/* Final Remark — editable dropdown, AI-suggested default */}
                      <td className="px-3 py-2.5 min-w-[140px]">
                        {(() => {
                          const auto = getFinalRemark(t);
                          const current = remarkOverrides[t.id] ?? auto?.label ?? '';
                          const remarkCls = {
                            'Income':          'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
                            'Expense':         'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
                            'Tax Payment':     'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
                            'Drawings':        'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
                            'Loan Repayment':  'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
                            'Unsecured Loan?': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
                            'Fund Transfer':   'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
                            'Review':          'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
                          };
                          return (
                            <select
                              value={current}
                              onChange={e => setRemarkOverrides(prev => ({ ...prev, [t.id]: e.target.value }))}
                              className={`w-full h-7 px-1.5 rounded-md border text-[11px] font-semibold cursor-pointer transition-all duration-100 focus:outline-none focus:ring-1 focus:ring-sky-500 shadow-sm ${remarkCls[current] || 'bg-slate-100 text-slate-600 border-slate-200'}`}
                              title="Final Remark — editable"
                            >
                              <option value="">— Remark —</option>
                              <option value="Income">Income</option>
                              <option value="Expense">Expense</option>
                              <option value="Tax Payment">Tax Payment</option>
                              <option value="Drawings">Drawings</option>
                              <option value="Loan Repayment">Loan Repayment</option>
                              <option value="Unsecured Loan?">Unsecured Loan?</option>
                              <option value="Fund Transfer">Fund Transfer</option>
                              <option value="Review">Review</option>
                            </select>
                          );
                        })()}
                      </td>

                      {/* L6 Dr/Cr */}
                      <td className="px-3 py-2.5">
                        <DrCrBadge value={t.category_l6} />
                      </td>

                      {/* L7 Payment method */}
                      <td className="px-3 py-2.5">
                        <L7Badge method={t.category_l7} />
                      </td>

                      {/* Confidence */}
                      <td className="px-3 py-2.5">
                        <ConfBadge label={t.confidence_label} />
                      </td>

                      {/* Status */}
                      <td className="px-3 py-2.5">
                        <StatusIcon
                          status={t.status}
                          aiVerified={t.ai_verified}
                          correctionNote={t.correction_note}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>

              {/* Totals footer */}
              <tfoot>
                <tr className="border-t border-gray-200/80 dark:border-slate-700/60 bg-[#F5F5F7] dark:bg-slate-800/80 font-semibold">
                  <td colSpan={3} className="px-3 py-3 text-xs text-slate-500 dark:text-slate-400">
                    {filtered.length.toLocaleString()} transactions · page {page} of {totalPages || 1}
                  </td>
                  <td className="px-3 py-3 text-right text-red-600 dark:text-red-400 tabular-nums">
                    {fmtINR(filteredTotals.debits)}
                  </td>
                  <td className="px-3 py-3 text-right text-emerald-600 dark:text-emerald-400 tabular-nums">
                    {fmtINR(filteredTotals.credits)}
                  </td>
                  <td className="px-3 py-3 text-right text-slate-600 dark:text-slate-400 text-xs tabular-nums">
                    Net: {fmtINR(filteredTotals.credits - filteredTotals.debits)}
                  </td>
                  <td colSpan={9} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* ── Pagination ───────────────────────────────────────────────────────── */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mb-6">
          <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => i + 1).map(p => (
            <button
              key={p}
              onClick={() => setPage(p)}
              className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors
                ${page === p
                  ? 'bg-sky-600 text-white shadow-sm'
                  : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400'}`}
            >
              {p}
            </button>
          ))}
          {totalPages > 10 && <span className="text-slate-400 text-sm">…{totalPages}</span>}
          <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* ── L1–L7 Glossary ───────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200/80 dark:border-slate-700 overflow-hidden mb-4 shadow-sm">
        <button
          className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
          onClick={() => setShowGlossary(v => !v)}
        >
          <div className="flex items-center gap-2">
            <Info className="w-4 h-4 text-sky-600" />
            <span className="font-semibold text-slate-900 dark:text-slate-100 text-sm">L1–L7 Taxonomy Glossary</span>
          </div>
          {showGlossary
            ? <ChevronUp className="w-4 h-4 text-slate-400" />
            : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </button>
        {showGlossary && (
          <div className="px-6 pb-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 border-t border-slate-100 dark:border-slate-800 pt-4">
            {[
              { l:'L1', t:'Financial Statement', d:'P&L A/c or Balance Sheet — read-only, derived from L2.' },
              { l:'L2', t:'Category ✎',          d:'Income / Expenditure / Assets / Liabilities / Capital.' },
              { l:'L3', t:'Sub-Type ✎',           d:'Direct Income, Indirect Expenses, Fixed Assets, etc.' },
              { l:'L4', t:'Tally Group ✎',        d:'Exact Tally group. Changing this auto-fills L1–L3.' },
              { l:'L5', t:'Tally Ledger ✎',       d:'Primary edit. Changing this auto-fills L1–L4.' },
              { l:'L6', t:'Dr / Cr',              d:'Whether the ledger is debited or credited in the voucher.' },
              { l:'L7', t:'Payment Method',       d:'UPI, NEFT, RTGS, ATM, Cheque… detected from narration.' },
            ].map(({ l, t, d }) => (
              <div key={l} className="flex gap-3">
                <span className="w-8 h-8 rounded-lg bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center text-xs font-bold text-sky-700 dark:text-sky-400 flex-shrink-0">{l}</span>
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{t}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{d}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Mapping Rules ────────────────────────────────────────────────────── */}
      <div className="mb-6">
        <Button variant="outline" size="sm"
          onClick={async () => { await loadRules(); setShowRules(true); }}
          className="gap-1.5">
          <Database className="w-4 h-4" /> View Mapping Rules ({rules.length})
        </Button>
      </div>

      <Dialog open={showRules} onClose={() => setShowRules(false)}
        title="Saved Mapping Rules"
        description="Learned from your manual corrections — applied automatically to new uploads.">
        {rules.length === 0 ? (
          <p className="text-sm text-slate-500 py-4 text-center">
            No mapping rules yet. Edit ledgers above to create rules.
          </p>
        ) : (
          <div className="max-h-80 overflow-y-auto space-y-2">
            {rules.map(r => (
              <div key={r.id} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-slate-900 dark:text-slate-100 truncate">{r.source_description}</p>
                  <p className="text-xs text-slate-500">{r.ledger} · {r.tally_group}</p>
                </div>
                <button
                  onClick={async () => {
                    await api.delete(`/mapping-rules?source_description=${encodeURIComponent(r.source_description)}`);
                    setRules(prev => prev.filter(x => x.id !== r.id));
                    toast.success('Rule deleted');
                  }}
                  className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </Dialog>

      </div>{/* end w-full min-w-0 wrapper */}

    </DashboardLayout>

    {/* AI Chat Assistant — floats over the page, memo-wrapped to prevent re-renders */}
    <ChatPanel statementId={id} bankName={bankName} accountNumber={acct} />
    </>
  );
}
